import React, { useState, useEffect } from 'react';
import { FiX, FiUser, FiMail, FiCheckCircle, FiXCircle, FiChevronDown, FiChevronUp } from 'react-icons/fi';
import { userRequest } from '../utils/requestMethods';

const SubjectDetailModal = ({ isOpen, onClose, subjectData, classId, schoolId }) => {
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expandedPermissions, setExpandedPermissions] = useState({});

  useEffect(() => {
    if (isOpen && subjectData?._id && classId && schoolId) {
      fetchSubjectTeachers();
      setExpandedPermissions({}); // Reset expanded permissions when modal opens
    } else if (!isOpen) {
      setExpandedPermissions({}); // Reset when modal closes
    }
  }, [isOpen, subjectData?._id, classId, schoolId]);

  const fetchSubjectTeachers = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await userRequest.get(`/teachers/subject-teachers/${schoolId}/${classId}/${subjectData._id}`);
      setTeachers(data.teachers || []);
    } catch (error) {
      console.error('Error fetching subject teachers:', error);
      setError('Failed to load teachers');
      setTeachers([]);
    } finally {
      setLoading(false);
    }
  };

  const getRoleBadge = (role) => {
    const roleColors = {
      primary: 'bg-blue-100 text-blue-800 border-blue-200',
      assistant: 'bg-purple-100 text-purple-800 border-purple-200',
      coordinator: 'bg-green-100 text-green-800 border-green-200',
    };

    return (
      <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border ${
        roleColors[role] || 'bg-gray-100 text-gray-800 border-gray-200'
      }`}>
        {role?.charAt(0).toUpperCase() + role?.slice(1) || 'Teacher'}
      </span>
    );
  };

  const getStatusBadge = (status) => {
    if (status === 'active') {
      return (
        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
          <FiCheckCircle className="w-3 h-3 mr-1" />
          Active
        </span>
      );
    }
    return (
      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
        <FiXCircle className="w-3 h-3 mr-1" />
        Inactive
      </span>
    );
  };

  const formatPermission = (permission) => {
    return permission
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const togglePermissions = (assignmentId) => {
    setExpandedPermissions(prev => ({
      ...prev,
      [assignmentId]: !prev[assignmentId]
    }));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden shadow-xl">
        {/* Header */}
        <div className="p-6 border-b border-gray-200 bg-gradient-to-r from-purple-50 to-indigo-50">
          <div className="flex justify-between items-start">
            <div className="flex-1">
              <h2 className="text-2xl font-bold text-slate-800 mb-2">
                {subjectData?.subject_name || 'Subject Details'}
              </h2>
              <div className="flex flex-wrap items-center gap-3 text-sm text-slate-600">
                {subjectData?.subject_description && (
                  <>
                    <span>
                      <span className="font-medium">Description:</span> {subjectData.subject_description}
                    </span>
                    <span>•</span>
                  </>
                )}
                <span>
                  <span className="font-medium">Pass Mark:</span> {subjectData?.pass_mark || 'N/A'}%
                </span>
                {subjectData?.subject_code && (
                  <>
                    <span>•</span>
                    <span>
                      <span className="font-medium">Code:</span> {subjectData.subject_code}
                    </span>
                  </>
                )}
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg p-2 transition-colors"
            >
              <FiX className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
          {/* Loading State */}
          {loading && (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
              <span className="ml-3 text-slate-600">Loading teachers...</span>
            </div>
          )}

          {/* Error State */}
          {error && !loading && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-center">
                <FiXCircle className="w-5 h-5 text-red-600 mr-2" />
                <p className="text-red-800">{error}</p>
              </div>
            </div>
          )}

          {/* Teachers List */}
          {!loading && !error && (
            <>
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-slate-800 mb-4">
                  Assigned Teachers ({teachers.length})
                </h3>

                {teachers.length === 0 ? (
                  <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
                    <FiUser className="w-12 h-12 mx-auto text-gray-400 mb-3" />
                    <p className="text-gray-500 font-medium">No teachers assigned</p>
                    <p className="text-sm text-gray-400 mt-1">This subject doesn't have any assigned teachers yet.</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {teachers.map((assignment) => (
                      <div
                        key={assignment.assignmentId || assignment.teacher._id}
                        className="bg-white border-2 border-gray-200 rounded-xl p-5 hover:border-purple-300 hover:shadow-md transition-all"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            {/* Teacher Info */}
                            <div className="flex items-center space-x-3 mb-3">
                              <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                                <FiUser className="w-6 h-6 text-purple-600" />
                              </div>
                              <div className="flex-1">
                                <h4 className="font-semibold text-slate-800 text-lg">
                                  {assignment.teacher?.firstname || 'N/A'} {assignment.teacher?.lastname || 'N/A'}
                                </h4>
                                <div className="flex items-center mt-1 text-sm text-slate-600">
                                  <FiMail className="w-4 h-4 mr-1" />
                                  <span>{assignment.teacher?.email || 'No email'}</span>
                                </div>
                              </div>
                            </div>

                            {/* Role and Status */}
                            <div className="flex items-center gap-3 mb-4">
                              {getRoleBadge(assignment.role)}
                              {getStatusBadge(assignment.status)}
                            </div>

                            {/* Permissions */}
                            {assignment.permissions && assignment.permissions.length > 0 && (
                              <div>
                                <button
                                  onClick={() => togglePermissions(assignment.assignmentId || assignment.teacher._id)}
                                  className="flex items-center justify-between w-full text-sm font-medium text-slate-700 mb-2 hover:text-slate-900 transition-colors"
                                >
                                  <span>Permissions ({assignment.permissions.length}):</span>
                                  {expandedPermissions[assignment.assignmentId || assignment.teacher._id] ? (
                                    <FiChevronUp className="w-4 h-4" />
                                  ) : (
                                    <FiChevronDown className="w-4 h-4" />
                                  )}
                                </button>
                                <div 
                                  className={`overflow-hidden transition-all duration-300 ease-in-out ${
                                    expandedPermissions[assignment.assignmentId || assignment.teacher._id] 
                                      ? 'max-h-96 opacity-100' 
                                      : 'max-h-0 opacity-0'
                                  }`}
                                >
                                  <div className="flex flex-wrap gap-2 mt-2">
                                    {assignment.permissions.map((permission, idx) => (
                                      <span
                                        key={idx}
                                        className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-slate-100 text-slate-700 border border-slate-200"
                                      >
                                        {formatPermission(permission)}
                                      </span>
                                    ))}
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Subject Information Summary */}
              <div className="mt-6 p-4 bg-gray-50 border border-gray-200 rounded-lg">
                <h4 className="font-medium text-slate-800 mb-3">Subject Summary</h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div>
                    <span className="text-slate-600">Total Teachers:</span>
                    <p className="font-semibold text-slate-800">{teachers.length}</p>
                  </div>
                  <div>
                    <span className="text-slate-600">Primary Teachers:</span>
                    <p className="font-semibold text-slate-800">
                      {teachers.filter(t => t.role === 'primary').length}
                    </p>
                  </div>
                  <div>
                    <span className="text-slate-600">Assistant Teachers:</span>
                    <p className="font-semibold text-slate-800">
                      {teachers.filter(t => t.role === 'assistant').length}
                    </p>
                  </div>
                  <div>
                    <span className="text-slate-600">Active:</span>
                    <p className="font-semibold text-slate-800">
                      {teachers.filter(t => t.status === 'active').length}
                    </p>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end items-center p-6 border-t border-gray-200 bg-gray-50">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-indigo-800 text-white rounded-lg hover:bg-indigo-700 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default SubjectDetailModal;

